import { useState, useEffect } from "react"
import ConfirmatorCard from "../../elements/confirmator_card"
import ConfirmatorList from "../../elements/confirmator_list"
import {Link} from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { BASE_URL } from '../../apiConfig';
import Loading from "../../cors/Loading";
import { toast } from 'sonner';
import { useLanguage } from "../../context/LanguageProvider";
import { HandCoins } from 'lucide-react';

export default function ConfirmatorsPage() {
  const { token, role, roles } = useAuth();
  const { t } = useLanguage();
  const [confirmators, setConfirmators] = useState([]); 
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("name");
  const [showSortDropdown, setShowSortDropdown] = useState(false); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null); 

  const fetchConfirmators = async (type) => {
    if (type === 'normal') {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/api/users-confirmators`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} - ${errorText}`);
        throw new Error(t('confirmators.error_fetch_msg', { status: response.statusText }));
      }
      
      const data = await response.json();
      
      if (data && Array.isArray(data.data)) {
        setConfirmators(data.data);
      } else {
        console.error("API response for confirmators is not an array:", data);
        setConfirmators([]);
        toast.error(t('confirmators.unexpected_format'));
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message || t('confirmators.loading_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfirmators('normal');
  }, []);

  const filteredConfirmators = confirmators
    .filter(
      (confirmator) =>
        confirmator.info?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        confirmator.email?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.info?.name?.localeCompare(b.info?.name || '');
        case "email":
          return a.email?.localeCompare(b.email || '');
        case "price_high":
          return (parseFloat(b.info?.price_confirm || 0) - parseFloat(a.info?.price_confirm || 0));
        case "price_low":
          return (parseFloat(a.info?.price_confirm || 0) - parseFloat(b.info?.price_confirm || 0));
        case "newest":
          return new Date(b.created_at) - new Date(a.created_at);
        case "oldest":
          return new Date(a.created_at) - new Date(b.created_at);
        default:
          return 0;
      }
    });

  // Calculate stats safely, handling empty array case
  const totalConfirmators = confirmators.length;
  const totalEarnings = confirmators.reduce((sum, c) => sum + parseFloat(c.info?.price_confirm || 0), 0);
  const avgPrice = totalConfirmators > 0 ? totalEarnings / totalConfirmators : 0;
  const highestPrice = totalConfirmators > 0 ? 
    Math.max(...confirmators.map((c) => parseFloat(c.info?.price_confirm || 0))) : 0;

  const now = new Date();
  const newThisMonth = confirmators.filter((confirmator) => {
    const created = new Date(confirmator.created_at);
    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 max">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              {t('confirmators.page_title')}
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              {t('confirmators.page_subtitle')}
            </p>
          </div>
          {
            (role === 1 || (role === 2 && roles.add_confirmator === 1)) &&
              <div className="mt-6 md:mt-0 flex gap-3">
                <Link to="/users/create">
                  <button className="cursor-pointer text-xs max-xl:py-3 bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold max-xl:py-2 max-xl:px-4 py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                    <span className="flex items-center">
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('confirmators.add')}
                    </span>
                  </button>
                </Link>
              </div>
          }
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('confirmators.total')}</p>
                <p className="text-3xl font-bold text-[var(--color-primary-500)]">{totalConfirmators}</p>
              </div>
              <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('confirmators.avg_rate')}</p>
                <p className="text-3xl font-bold text-blue-600">{avgPrice.toFixed(2)} MAD</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <HandCoins className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('confirmators.highest_rate')}</p>
                <p className="text-3xl font-bold text-green-600">
                  {highestPrice.toFixed(2)} MAD
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('confirmators.new_this_month')}</p>
                <p className="text-3xl font-bold text-purple-600">{newThisMonth}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-5">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            {/* Search Bar */}
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t('confirmators.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full dark:bg-stone-900 dark:text-white outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>

            {/* Controls */}
            <div className="flex gap-4 max-xl:w-full">
              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex text-xs dark:bg-stone-900 cursor-pointer items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-all duration-200"
                >
                  <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                    />
                  </svg>
                  <span className="text-xs dark:text-gray-300">{t('common.sort_by')}</span>
                  <svg
                    className={`h-3 w-3 text-gray-500 transition-transform ${showSortDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showSortDropdown && (
                  <div className="absolute cursor-pointer top-full mt-2 left-0 w-48 bg-white border-2 border-gray-200 rounded-2xl shadow-xl z-10">
                    <div className="py-2 text-xs ">
                      {[
                        { value: "name", label: t('confirmators.sort_name') },
                        { value: "email", label: t('confirmators.sort_email') },
                        { value: "price_high", label: t('confirmators.sort_rate_high') },
                        { value: "price_low", label: t('confirmators.sort_rate_low') },
                        { value: "newest", label: t('common.newest_first') },
                        { value: "oldest", label: t('common.oldest_first') },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            sortBy === option.value ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium" : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 max-h-max rounded-xl ml-2 overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "grid" ? "bg-[var(--color-primary-500)] text-white" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "list" ? "bg-[var(--color-primary-500)] text-white" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading and error feedback */}
        {loading && (
          <div className="flex justify-center text-[var(--color-primary-500)] text-5xl mt-40">
            <Loading />
          </div>
        )}
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            {/* Results Count */}
            <div className="mb-8">
              <span className="inline-flex items-center px-4 py-2 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-[10px] font-medium">
                {t('confirmators.found_count', { count: filteredConfirmators.length })}
              </span>
            </div>

            {/* Confirmators Display */}
            {filteredConfirmators.length > 0 ? (
              viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                  {filteredConfirmators.map((confirmator) => (
                    <ConfirmatorCard key={confirmator.id} confirmator={confirmator} fetchConfirmators={fetchConfirmators} />
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredConfirmators.map((confirmator) => (
                    <ConfirmatorList key={confirmator.id} confirmator={confirmator} fetchConfirmators={fetchConfirmators} />
                  ))}
                </div>
              )
            ) : (
              /* Empty State */
              <div className="text-center">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-12 w-12 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('confirmators.no_results_title')}</h3>
                <p className="text-gray-600 text-sm mb-6">
                  {t('common.adjust_search')}
                </p>
                {
                  (role === 1 || (role === 2 && roles.add_confirmator === 1)) &&
                    <Link to="/users/create">
                      <button className="bg-[var(--color-primary-500)] cursor-pointer text-sm hover:bg-[var(--color-primary-400)] text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300">
                        {t('confirmators.add_first')}
                      </button>
                    </Link>
                }
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}